import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'
import { Play } from 'lucide-react'

import {
  getCallAudioUrl,
  listAuctionNotifyLog,
  type AuctionNotifyKind,
  type AuctionNotifyRow,
} from '@/api/auction-notify-log'
import { listAuctionSmsRecipients, type AuctionSmsRecipient } from '@/api/auction-sms'
import { PageHeader } from '@/components/layout/PageHeader'
import { Loader } from '@/components/ui/Loader'
import { Button } from '@/components/ui/Button'
import { SegmentedControl } from '@/components/ui/SegmentedControl'
import { TestBadge } from '@/components/inspections/LeadBadges'
import { emptyTableCellClassName } from '@/constants/ui'
import { dateFnsLocale } from '@/utils/dateLocale'
import { formatPhoneDisplay } from '@/utils/phone'

type KindFilter = 'all' | AuctionNotifyKind

const LIMIT = 200

/**
 * /sber-notify-log: журнал SMS и звонков дилерам о старте торгов. SMS из
 * sms_outbox (source auction_start), звонки из call_outbox с результатом,
 * длительностью и записью сообщения (bucket call-audio). Миграции stryzh
 * 20261103160000, 20261105220000. Тест из карточки лида (source auction_test,
 * 20261105234500) с меткой «ТЕСТ».
 */
export function SberNotifyLogPage() {
  const { t } = useTranslation()
  const [kind, setKind] = useState<KindFilter>('all')

  const logQ = useQuery({
    queryKey: ['auction-notify-log', kind],
    queryFn: () => listAuctionNotifyLog({ kind: kind === 'all' ? undefined : kind, limit: LIMIT }),
    refetchInterval: 15_000,
  })
  const rcpQ = useQuery({ queryKey: ['auction-sms-recipients'], queryFn: listAuctionSmsRecipients })
  const byPhone = useMemo(() => new Map((rcpQ.data ?? []).map((r) => [r.phone, r])), [rcpQ.data])

  return (
    <div className="w-full max-w-none px-6 py-4 lg:px-8">
      <PageHeader title={t('logs.sberNotify.title')} />

      <div className="mb-4 flex flex-wrap items-end gap-2">
        <SegmentedControl<KindFilter>
          aria-label={t('logs.sberNotify.kindLabel')}
          value={kind}
          onChange={setKind}
          options={[
            { value: 'all', label: t('logs.sberNotify.kindAll') },
            { value: 'sms', label: t('logs.sberNotify.kindSms') },
            { value: 'call', label: t('logs.sberNotify.kindCall') },
          ]}
        />
        <Button type="button" variant="ghost" onClick={() => void logQ.refetch()} disabled={logQ.isFetching}>
          {logQ.isFetching ? t('logs.sberNotify.refreshing') : t('logs.sberNotify.refresh')}
        </Button>
      </div>

      {logQ.error ? (
        <div className="rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-200">
          {(logQ.error as Error).message}
        </div>
      ) : null}

      <div className="overflow-x-auto rounded-xl border border-[var(--stryzh-color-border-default)]">
        <table className="w-full min-w-[1100px] text-left text-sm">
          <thead className="bg-[var(--stryzh-color-surface)] text-[var(--stryzh-color-text-secondary)]">
            <tr>
              <th className="px-3 py-2 whitespace-nowrap">{t('logs.sberNotify.colWhen')}</th>
              <th className="px-3 py-2 whitespace-nowrap">{t('logs.sberNotify.colKind')}</th>
              <th className="px-3 py-2 whitespace-nowrap">{t('logs.sberNotify.colRecipient')}</th>
              <th className="px-3 py-2 whitespace-nowrap">{t('logs.sberNotify.colLot')}</th>
              <th className="px-3 py-2 whitespace-nowrap">{t('logs.sberNotify.colResult')}</th>
              <th className="px-3 py-2 whitespace-nowrap">{t('logs.sberNotify.colDuration')}</th>
              <th className="px-3 py-2">{t('logs.sberNotify.colText')}</th>
              <th className="px-3 py-2 whitespace-nowrap">{t('logs.sberNotify.colAudio')}</th>
            </tr>
          </thead>
          <tbody>
            {logQ.isLoading ? (
              <tr>
                <td colSpan={8} className={emptyTableCellClassName}>
                  <Loader centered size="sm" />
                </td>
              </tr>
            ) : !logQ.data || logQ.data.length === 0 ? (
              <tr>
                <td colSpan={8} className={emptyTableCellClassName}>
                  {t('common.noData')}
                </td>
              </tr>
            ) : (
              logQ.data.map((row) => (
                <LogRow key={`${row.kind}-${row.id}`} row={row} recipient={byPhone.get(row.phone) ?? null} />
              ))
            )}
          </tbody>
        </table>
      </div>

      <p className="mt-3 text-xs text-[color:var(--stryzh-color-text-secondary)]">
        {t('logs.sberNotify.footer', { n: LIMIT })}
      </p>
    </div>
  )
}

const STATUS_TONE: Record<string, string> = {
  sent: 'text-green-300',
  answered: 'text-green-300',
  queued: 'text-blue-300',
  calling: 'text-blue-300',
  no_answer: 'text-yellow-300',
  busy: 'text-yellow-300',
  rejected: 'text-yellow-300',
  expired: 'text-[color:var(--stryzh-color-text-secondary)]',
  skipped: 'text-[color:var(--stryzh-color-text-secondary)]',
  failed: 'text-red-300',
}

const muted = 'text-[color:var(--stryzh-color-text-secondary)]'

function LogRow({ row, recipient }: { row: AuctionNotifyRow; recipient: AuctionSmsRecipient | null }) {
  const { t } = useTranslation()
  const locale = dateFnsLocale()
  const at = format(new Date(row.at), 'd MMM HH:mm', { locale })
  // Поставлен вне окна звонков: показываем, с какого времени ждёт.
  const waiting =
    row.kind === 'call' && row.status === 'queued' && row.notBefore != null && Date.parse(row.notBefore) > Date.parse(row.at) + 60_000
  const car = row.lead
    ? [row.lead.vehicle_brand, row.lead.vehicle_model, row.lead.vehicle_year].filter((x) => x != null && x !== '').join(' ')
    : ''
  // skipped: причина «выключено» уже в подписи статуса, техническую строку не показываем.
  const detail = row.status === 'skipped' ? null : [row.sipCode != null ? `SIP ${row.sipCode}` : null, row.detail].filter(Boolean).join(', ')

  return (
    <tr className="border-t border-[color:var(--stryzh-color-border-default)] align-top">
      <td className={`px-3 py-2 whitespace-nowrap font-[family-name:var(--stryzh-font-family-mono)] ${muted}`}>{at}</td>
      <td className="px-3 py-2 whitespace-nowrap">
        <div>{t(`logs.sberNotify.kind.${row.kind}`)}</div>
        {row.isTest ? (
          <div className="mt-1">
            <TestBadge />
          </div>
        ) : null}
      </td>
      <td className="px-3 py-2 whitespace-nowrap">
        {recipient ? (
          <div>
            {recipient.full_name}
            <span className={`ml-1 text-xs ${muted}`}>{recipient.dealer}</span>
          </div>
        ) : null}
        <div className={`text-xs tabular-nums ${muted}`}>{formatPhoneDisplay('+' + row.phone)}</div>
      </td>
      <td className="px-3 py-2 whitespace-nowrap">
        {row.lead ? (
          <Link to={`/inspection-leads/${row.lead.id}`} className="text-[color:var(--stryzh-color-accent-orange)] hover:underline">
            {car || row.lead.id.slice(0, 8)}
          </Link>
        ) : (
          <span className={muted}>-</span>
        )}
      </td>
      <td className="px-3 py-2">
        <div className={`whitespace-nowrap ${STATUS_TONE[row.status] ?? ''}`}>
          {t(`logs.sberNotify.status.${row.kind}.${row.status}`)}
        </div>
        {waiting && row.notBefore ? (
          <div className={`text-xs whitespace-nowrap ${muted}`}>
            {t('logs.sberNotify.waitWindow', { at: format(new Date(row.notBefore), 'd MMM HH:mm', { locale }) })}
          </div>
        ) : null}
        {detail ? <div className={`max-w-[220px] truncate text-xs ${muted}`} title={detail}>{detail}</div> : null}
      </td>
      <td className="px-3 py-2 whitespace-nowrap tabular-nums">
        {row.durationSec != null ? formatDuration(row.durationSec) : <span className={muted}>-</span>}
      </td>
      <td className="max-w-[420px] px-3 py-2 text-xs">{row.message}</td>
      <td className="px-3 py-2 whitespace-nowrap">
        {row.audioPath ? <AudioCell path={row.audioPath} /> : <span className={muted}>-</span>}
      </td>
    </tr>
  )
}

/** 75 → «1:15». */
function formatDuration(sec: number): string {
  const s = Math.max(0, Math.round(sec))
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

/** Запись сообщения: ссылка на файл запрашивается по нажатию, потом обычный плеер. */
function AudioCell({ path }: { path: string }) {
  const { t } = useTranslation()
  const [url, setUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  if (url) return <audio controls autoPlay src={url} className="h-8 w-56" />
  return (
    <div>
      <button
        type="button"
        disabled={loading}
        onClick={() => {
          setLoading(true)
          setError(null)
          getCallAudioUrl(path)
            .then(setUrl)
            .catch((e: Error) => setError(e.message))
            .finally(() => setLoading(false))
        }}
        className="inline-flex items-center gap-1 text-[color:var(--stryzh-color-accent-orange)] hover:underline disabled:opacity-50"
      >
        <Play size={14} aria-hidden />
        {t('logs.sberNotify.play')}
      </button>
      {error ? <div className="text-xs text-red-300">{error}</div> : null}
    </div>
  )
}
